import React, { PropsWithChildren } from 'react';
import * as Sentry from '@sentry/browser';
import {
  FpjsProvider,
  useVisitorData,
} from '@fingerprintjs/fingerprintjs-pro-react';
import { setFingerprintRequestId } from 'signer-app/utils/hs-fetch';

type Props = {
  enabled: boolean;
};

export type FingerprintJSData = {
  requestId: string;
};

export interface FingerprintJSContextInterface {
  getFingerprintData: () => Promise<FingerprintJSData>;
}

export const FingerprintJSContext =
  React.createContext<FingerprintJSContextInterface>({
    getFingerprintData: () => Promise.resolve({ requestId: '' }),
  });

export const useFingerprintJS = () => {
  const context = React.useContext(FingerprintJSContext);

  if (context) {
    return context;
  } else {
    throw new Error(
      'useFingerprintJS must be used within a FingerprintJSProvider',
    );
  }
};

export const FingerprintJSProvider = ({
  children,
  ...props
}: PropsWithChildren<{}> & Props) => {
  return (
    <FpjsProvider
      loadOptions={{
        apiKey: FPJS_PUBLIC_KEY,
        endpoint: FPJS_ENDPOINT,
        scriptUrlPattern: FPJS_CDN_ENDPOINT,
        urlHashing: {
          path: true,
          query: true,
          fragment: true,
        },
      }}
      cacheTimeInSeconds={3600}
    >
      <FingerprintJSInternalProvider {...props}>
        {children}
      </FingerprintJSInternalProvider>
    </FpjsProvider>
  );
};

export const FingerprintJSConsumer = FingerprintJSContext.Consumer;

const FingerprintJSInternalProvider = ({
  children,
  enabled,
}: PropsWithChildren<{}> & Props) => {
  const { getData } = useVisitorData(
    {},
    {
      immediate: false,
    },
  );

  // See: https://github.com/fingerprintjs/fingerprintjs-pro-react/issues/132
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getDataMemo = React.useMemo(() => getData, []);

  const getFingerprintData = React.useCallback(async () => {
    if (!enabled) {
      return { requestId: '' };
    }
    try {
      const visitorData = await getDataMemo({ ignoreCache: false });
      setFingerprintRequestId(visitorData.requestId);
      return { requestId: visitorData.requestId };
    } catch (err) {
      Sentry.captureException(err);
      return { requestId: '' };
    }
  }, [getDataMemo, enabled]);

  const context = React.useMemo(
    () => ({ getFingerprintData }),
    [getFingerprintData],
  );

  return (
    <FingerprintJSContext.Provider value={context}>
      {children}
    </FingerprintJSContext.Provider>
  );
};
